import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { NewApiBaseUrl } from '../consts/new-api-base-url'
import useHttpApiNew from '../hooks/useHttpApiNew'
import { shouldSkipOnlineApiCalls } from '../offline/offline-session-utils'
import { canUserAccessActionHistory } from '../pages/Statistics/statistics-report-type'
// eslint-disable-next-line no-restricted-globals
const newLogsBaseUrl = new URL(`${NewApiBaseUrl}/ActionLog`)
const newUnreadCountUrl = new URL(`${NewApiBaseUrl}/ActionLog/unread-count`)

// Step 1: Create Context (default value is optional)
const ActionLogsContext = createContext()

function resolveToken(location) {
  if (location.state?.token) return location.state.token
  try {
    const raw = localStorage.getItem('adams_user')
    const user = raw ? JSON.parse(raw) : null
    return user?.token || localStorage.getItem('token') || null
  } catch {
    return localStorage.getItem('token') || null
  }
}

function resolveInit(location) {
  if (location.state?.init) return location.state.init
  try {
    const raw = localStorage.getItem('adams_user')
    const user = raw ? JSON.parse(raw) : null
    return user?.init || null
  } catch {
    return null
  }
}

// Step 2: Create Provider Component
export const ActionLogsProvider = ({ children }) => {
  const location = useLocation()
  const token = resolveToken(location)
  const init = resolveInit(location)
  const [count, setCount] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const { request } = useHttpApiNew()

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await request(newUnreadCountUrl, 'GET', null, {
        Authorization: `Bearer ${token}`
      })
      if (response.responseAnyError) return
      const nextCount = response.responseData?.count
      setCount(typeof nextCount === 'number' ? nextCount : 0)
    } catch {
      setCount(0)
    } finally {
      setIsLoading(false)
    }
  }, [token, request])

  /** Asks the open Notification Center to reload and refreshes the unread badge. */
  const fetchLogs = useCallback(() => {
    setRefreshKey(previous => previous + 1)
    fetchUnreadCount()
  }, [fetchUnreadCount])

  useEffect(() => {
    if (location.pathname === '/signup') return
    if (shouldSkipOnlineApiCalls()) {
      setIsLoading(false)
      return
    }
    if (!canUserAccessActionHistory(init?.user)) {
      setIsLoading(false)
      return
    }
    const userType = init?.user?.type
    if (!(token && userType !== undefined && userType !== null && userType >= 0)) return

    fetchUnreadCount()
  }, [token, init?.user, location.pathname, location.state?.isHistoryOpen, fetchUnreadCount])

  // Update function (can be called from nested components)
  const setAsRead = async id => {
    const response = await request(newLogsBaseUrl, 'POST', id, {
      Authorization: `Bearer ${token}`
    })
    if (response.responseAnyError) return;
    setCount((prev) => (typeof prev === 'number' ? Math.max(0, prev - 1) : prev))
  }

  return (
    <ActionLogsContext.Provider value={{ count, refreshKey, setAsRead, isLoading, fetchLogs, fetchUnreadCount }}>
      {children}
    </ActionLogsContext.Provider>
  )
}

// Step 3: Custom Hook for easy access
export const useActionLogs = () => {
  const context = useContext(ActionLogsContext)
  if (!context) throw new Error('useCount must be used within a NewLogsProvider')
  return context // Returns { count, setCount }
}
